import { useSessionStore } from "./sessionStore";
import { useCompanyUserStore } from "./companyUserStore";
import { useCompanyStore } from "./companyStore";
import { usePermissionsStore } from "./permissionsStore";
import { useEntitlementsStore } from "./entitlementsStore";
import { Session } from "@supabase/supabase-js";

/**
 * Coordinator for managing data flow between stores
 */
export class StoreCoordinator {
    private static instance: StoreCoordinator;
    private initialized = false;

    private constructor() {}

    public static getInstance(): StoreCoordinator {
        if (!StoreCoordinator.instance) {
            StoreCoordinator.instance = new StoreCoordinator();
        }
        return StoreCoordinator.instance;
    }

    /**
     * Initialize the coordinator and set up store subscriptions
     */
    public initialize() {
        if (this.initialized) return;

        let prevSessionId: string | null = null;
        let prevCompanyUserId: string | null = null;

        useSessionStore.subscribe((state) => {
            const currentSessionId = state.session?.user?.id || null;
            if (currentSessionId !== prevSessionId) {
                prevSessionId = currentSessionId;
                this.handleSessionChange(state.session);
            }
        });

        useCompanyUserStore.subscribe((state) => {
            const currentCompanyUserId = state.companyUser?.id || null;
            if (currentCompanyUserId !== prevCompanyUserId) {
                prevCompanyUserId = currentCompanyUserId;
                this.handleCompanyUserChange(state.companyUser);
            }
        });

        this.initialized = true;
    }

    private async handleSessionChange(session: Session | null) {
        if (session) {
            await useCompanyUserStore.getState().fetchCompanyUser();
        } else {
            useCompanyUserStore.getState().clearCompanyUserData();
            useCompanyStore.getState().clearCompanyData();
            usePermissionsStore.getState().clearPermissionsData();
            useEntitlementsStore.getState().clearEntitlementsData();
        }
    }

    private async handleCompanyUserChange(companyUser: {
        company_id: string;
    } | null) {
        if (companyUser) {
            useCompanyStore.getState().setCompanyId(companyUser.company_id);
            await Promise.all([
                usePermissionsStore.getState().fetchPermissions(),
                useEntitlementsStore.getState().fetchEntitlements(),
            ]);
        } else {
            useCompanyStore.getState().clearCompanyData();
            usePermissionsStore.getState().clearPermissionsData();
            useEntitlementsStore.getState().clearEntitlementsData();
        }
    }

    public setSession(session: Session | null) {
        useSessionStore.getState().setSession(session);
    }

    public setLoading(loading: boolean) {
        useSessionStore.getState().setLoading(loading);
    }

    public getIsLoading(): boolean {
        const sessionStore = useSessionStore.getState();
        const companyUserStore = useCompanyUserStore.getState();
        const permissionsStore = usePermissionsStore.getState();
        const entitlementsStore = useEntitlementsStore.getState();

        return sessionStore.isLoading ||
            companyUserStore.isCompanyUserLoading ||
            permissionsStore.isPermissionsLoading ||
            entitlementsStore.isEntitlementsLoading;
    }

    public getIsFullyAuthenticated(): boolean {
        return useCompanyUserStore.getState().isFullyAuthenticated();
    }
}

export const storeCoordinator = StoreCoordinator.getInstance();
